import {
	DndContext,
	DragOverlay,
	MeasuringStrategy,
	PointerSensor,
	pointerWithin,
	rectIntersection,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DragEndEvent,
	type DragOverEvent,
	type DragStartEvent,
} from '@dnd-kit/core';
import {
	horizontalListSortingStrategy,
	SortableContext,
} from '@dnd-kit/sortable';
import { Stack } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	HORIZONTAL_INDICATOR_ABOVE_ID_PREFIX,
	HORIZONTAL_INDICATOR_BELOW_ID_PREFIX,
	MAX_ITEMS_PER_ROW,
	MIN_ITEM_SPAN,
	TOTAL_NUMBER_OF_COLUMNS,
	VERTICAL_INDICATOR_AFTER_ID_PREFIX,
	VERTICAL_INDICATOR_BEFORE_ID_PREFIX,
} from './constants';
import { DnDItem as DnDItemComponent, DnDItemInternal } from './DnDItem';
import { DnDRow } from './DnDRow';
import {
	type DnDDragState,
	type DnDItem,
	type DnDLayoutItem,
	type DnDResizeState,
	type RenderItemArgs,
} from './types';
import {
	calculateItemSpansForRow,
	createItemsById,
	createItemsByRowId,
	createItemSpansById,
	createLayout,
	extractItemIdFromIndicator,
	findRowForIndicator,
	generateUniqueId,
} from './utils';

/**
 * Props for the DnD component
 */
export interface DnDProps<T> {
	/** Array of items to be displayed in the grid */
	items: DnDItem<T>[];
	/** Layout configuration defining row, order, and span for each item */
	layout: Record<string, DnDLayoutItem>;
	/**
	 * If true, disables all drag-and-drop and resize interactions.
	 * The grid will be rendered read-only.
	 */
	readOnly?: boolean;
	/** Callback fired when layout changes (optional) */
	onLayoutChange?: (layout: Record<string, DnDLayoutItem>) => void;
	/** Function to render each item */
	renderItem: (args: RenderItemArgs<T>) => React.ReactNode;
}

/**
 * Drag and Drop Grid Component
 *
 * A flexible grid system that supports:
 * - Drag and drop reordering within and between rows
 * - Resizing items with smooth visual feedback
 * - Horizontal and vertical indicators for drop zones
 */
export function DnD<T>({
	items,
	layout,
	readOnly = false,
	onLayoutChange,
	renderItem,
}: DnDProps<T>) {
	// Initialize data structures from props
	const itemsById = useMemo(() => createItemsById(items), [items]);
	const { rowIds: initialRowIds, itemsByRowId: initialItemsByRowId } = useMemo(
		() => createItemsByRowId(items, layout),
		[items, layout]
	);
	const initialItemSpansById = useMemo(
		() => createItemSpansById(items, layout, initialItemsByRowId),
		[items, layout, initialItemsByRowId]
	);

	// State management
	const [rowIds, setRowIds] = useState<string[]>(initialRowIds);
	const [itemsByRowId, setItemsByRowId] =
		useState<Record<string, DnDItem<T>[]>>(initialItemsByRowId);
	const [itemSpansById, setItemSpansById] =
		useState<Record<string, number>>(initialItemSpansById);

	// Drag and drop state
	const [dragState, setDragState] = useState<DnDDragState>({
		activeId: null,
		activeRowId: null,
		activeIndex: null,
		overId: null,
		overRowId: null,
		overIndex: null,
		overType: null,
	});

	// Resize state for smooth visual feedback
	const [resizeState, setResizeState] = useState<DnDResizeState>({
		isResizing: false,
		resizeIndicatorId: null,
		resizeRowId: null,
		resizeItemIndex: null,
		initialSpans: null,
		initialMouseX: null,
		tempWidths: null,
	});

	const sensors = useSensors(useSensor(PointerSensor));

	/**
	 * Custom collision detection that prioritizes horizontal indicators
	 * for creating new rows, otherwise uses rectangle intersection detection
	 */
	const customCollisionDetectionAlgorithm: CollisionDetection = useCallback(
		(args) => {
			const horizontalPointerIntersections = pointerWithin({
				...args,
				droppableContainers: args.droppableContainers.filter(
					(container) =>
						container.id
							.toString()
							.startsWith(HORIZONTAL_INDICATOR_ABOVE_ID_PREFIX) ||
						container.id
							.toString()
							.startsWith(HORIZONTAL_INDICATOR_BELOW_ID_PREFIX)
				),
			});

			if (horizontalPointerIntersections.length > 0) {
				return horizontalPointerIntersections;
			}

			const verticalRectIntersections = rectIntersection({
				...args,
				droppableContainers: args.droppableContainers.filter(
					(container) =>
						container.id
							.toString()
							.startsWith(VERTICAL_INDICATOR_BEFORE_ID_PREFIX) ||
						container.id
							.toString()
							.startsWith(VERTICAL_INDICATOR_AFTER_ID_PREFIX)
				),
			});

			return verticalRectIntersections;
		},
		[]
	);

	/**
	 * Notifies parent component of layout changes
	 */
	const handleLayoutChange = useCallback(
		(
			newRowIds: string[],
			newItemsByRowId: Record<string, DnDItem<T>[]>,
			newItemSpansById: Record<string, number>
		) => {
			if (onLayoutChange) {
				onLayoutChange(
					createLayout(newRowIds, newItemsByRowId, newItemSpansById)
				);
			}
		},
		[onLayoutChange]
	);

	/**
	 * Finds which row contains the given item ID
	 */
	const findRow = useCallback(
		(id: string) => {
			if (id in itemsByRowId) {
				return id;
			}

			return Object.keys(itemsByRowId).find((rowID) =>
				itemsByRowId[rowID].some((item) => item.id === id)
			);
		},
		[itemsByRowId]
	);

	/**
	 * Determines the type of element based on its ID
	 */
	const findType = useCallback(
		(id: string) => {
			if (
				id.startsWith(VERTICAL_INDICATOR_BEFORE_ID_PREFIX) ||
				id.startsWith(VERTICAL_INDICATOR_AFTER_ID_PREFIX)
			) {
				return 'vertical-indicator';
			}
			if (
				id.startsWith(HORIZONTAL_INDICATOR_ABOVE_ID_PREFIX) ||
				id.startsWith(HORIZONTAL_INDICATOR_BELOW_ID_PREFIX)
			) {
				return 'horizontal-indicator';
			}
			if (id in rowIds) {
				return 'row';
			}

			return 'item';
		},
		[rowIds]
	);

	/**
	 * Calculates item width based on span or temporary resize state
	 * Uses tempWidths during resize for smooth visual feedback
	 */
	const getItemWidth = useCallback(
		(itemId: string) => {
			// Use temporary width during resize if available
			if (
				resizeState.isResizing &&
				resizeState.tempWidths &&
				resizeState.tempWidths[itemId]
			) {
				return resizeState.tempWidths[itemId];
			}

			const rowId = findRow(itemId);
			if (!rowId) return null;

			const span = itemSpansById[itemId];
			if (!span) return null;

			return `${(1 / (TOTAL_NUMBER_OF_COLUMNS / span)) * 100}%`;
		},
		[findRow, itemSpansById, resizeState.isResizing, resizeState.tempWidths]
	);

	/**
	 * Clears drag state when drag operation ends
	 */
	const handleClearDragState = useCallback(() => {
		setDragState({
			activeId: null,
			activeRowId: null,
			activeIndex: null,
			overId: null,
			overRowId: null,
			overIndex: null,
			overType: null,
		});
	}, []);

	/**
	 * Handles drag start event - initializes drag state
	 */
	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			if (readOnly) return;

			const activeId = event.active.id as string;
			const activeRowId = findRow(activeId);
			if (!activeRowId) {
				return;
			}

			const activeIndex = itemsByRowId[activeRowId].findIndex(
				(item) => item.id === event.active.id
			);

			setDragState({
				activeId: event.active.id as string,
				activeRowId,
				activeIndex,
				overId: null,
				overRowId: null,
				overIndex: null,
				overType: null,
			});
		},
		[readOnly, findRow, itemsByRowId]
	);

	/**
	 * Removes empty rows and updates row IDs
	 */
	const handleClearEmptyRows = useCallback(
		(
			newItemsByRowId: Record<string, DnDItem<T>[]>,
			newRowIds: string[]
		): string[] => {
			const updatedRowIds = newRowIds.filter(
				(rowId) => newItemsByRowId[rowId].length > 0
			);
			setRowIds(updatedRowIds);
			return updatedRowIds;
		},
		[]
	);

	/**
	 * Recalculates item spans only for the affected rows
	 */
	const handleUpdateItemSpans = useCallback(
		(
			newItemsByRowId: Record<string, DnDItem<T>[]>,
			fromRowId: string,
			toRowId: string
		): Record<string, number> => {
			// Start with existing spans
			const newItemSpansById = { ...itemSpansById };

			[fromRowId, toRowId].forEach((rowId) => {
				const rowItems = newItemsByRowId[rowId];
				if (rowItems && rowItems.length > 0) {
					const rowItemSpans = calculateItemSpansForRow(rowItems);
					Object.assign(newItemSpansById, rowItemSpans);
				}
			});

			setItemSpansById(newItemSpansById);
			return newItemSpansById;
		},
		[itemSpansById]
	);

	/**
	 * Extracts drop position data from vertical indicator ID
	 */
	const getVerticalIndicatorData = useCallback(
		(overId: string) => {
			let itemId: string;
			let isBefore: boolean;

			if (overId.startsWith(VERTICAL_INDICATOR_BEFORE_ID_PREFIX)) {
				itemId = overId.replace(`${VERTICAL_INDICATOR_BEFORE_ID_PREFIX}-`, '');
				isBefore = true;
			} else if (overId.startsWith(VERTICAL_INDICATOR_AFTER_ID_PREFIX)) {
				itemId = overId.replace(`${VERTICAL_INDICATOR_AFTER_ID_PREFIX}-`, '');
				isBefore = false;
			} else {
				return null;
			}

			const targetRowId = findRow(itemId);
			if (!targetRowId) {
				return null;
			}

			const targetItemIndex = itemsByRowId[targetRowId].findIndex(
				(item) => item.id === itemId
			);
			if (targetItemIndex === -1) {
				return null;
			}

			const insertionIndex = isBefore ? targetItemIndex : targetItemIndex + 1;

			return {
				overRowId: targetRowId,
				overIndex: insertionIndex,
			};
		},
		[findRow, itemsByRowId]
	);

	/**
	 * Extracts drop position data from horizontal indicator ID
	 */
	const getHorizontalIndicatorData = useCallback(
		(overId: string) => {
			let rowId: string;
			let isAbove: boolean;

			if (overId.startsWith(HORIZONTAL_INDICATOR_ABOVE_ID_PREFIX)) {
				rowId = overId.replace(`${HORIZONTAL_INDICATOR_ABOVE_ID_PREFIX}-`, '');
				isAbove = true;
			} else if (overId.startsWith(HORIZONTAL_INDICATOR_BELOW_ID_PREFIX)) {
				rowId = overId.replace(`${HORIZONTAL_INDICATOR_BELOW_ID_PREFIX}-`, '');
				isAbove = false;
			} else {
				return null;
			}

			const targetRowIndex = rowIds.findIndex((id) => id === rowId);
			if (targetRowIndex === -1) {
				return null;
			}

			const insertionIndex = isAbove ? targetRowIndex : targetRowIndex + 1;

			return {
				overRowId: null,
				overIndex: insertionIndex,
			};
		},
		[rowIds]
	);

	/**
	 * Handles drag over event - updates drag state with drop position
	 */
	const handleDragOver = useCallback(
		({ over }: DragOverEvent) => {
			if (readOnly || !over) {
				return;
			}
			const overId = over.id as string;
			const overType = findType(overId);

			let overRowId: string | null = null;
			let overIndex: number | null = null;

			if (overType === 'vertical-indicator') {
				const data = getVerticalIndicatorData(overId);
				if (!data) return;
				({ overRowId, overIndex } = data);
			}

			if (overType === 'horizontal-indicator') {
				const data = getHorizontalIndicatorData(overId);
				if (!data) return;
				({ overRowId, overIndex } = data);
			}

			setDragState((prev) => ({
				...prev,
				overId,
				overRowId,
				overIndex,
				overType,
			}));
		},
		[readOnly, findType, getVerticalIndicatorData, getHorizontalIndicatorData]
	);

	/**
	 * Handles drag end when item is dropped within the same row
	 */
	const handleDragEndOverSameRow = useCallback(
		(activeId: string, activeRowId: string, overIndex: number) => {
			const currentRowItems = [...itemsByRowId[activeRowId]];
			const activeIndex = currentRowItems.findIndex(
				(item) => item.id === activeId
			);

			if (activeIndex === -1) return;

			const [movedItem] = currentRowItems.splice(activeIndex, 1);

			const adjustedIndex = activeIndex < overIndex ? overIndex - 1 : overIndex;

			currentRowItems.splice(adjustedIndex, 0, movedItem);

			const newItemsByRowId = {
				...itemsByRowId,
				[activeRowId]: currentRowItems,
			};

			setItemsByRowId(newItemsByRowId);
			handleLayoutChange(rowIds, newItemsByRowId, itemSpansById);
		},
		[handleLayoutChange, itemSpansById, itemsByRowId, rowIds]
	);

	/**
	 * Handles drag end when item is dropped between different rows
	 */
	const handleDragEndBetweenRows = useCallback(
		(
			activeId: string,
			activeRowId: string,
			overRowId: string,
			overIndex: number
		) => {
			if (itemsByRowId[overRowId].length === MAX_ITEMS_PER_ROW) {
				handleClearDragState();
				return;
			}

			const newItemsByRowId = { ...itemsByRowId };

			const sourceRowItems = [...newItemsByRowId[activeRowId]];
			const activeIndex = sourceRowItems.findIndex(
				(item) => item.id === activeId
			);
			const [movedItem] = sourceRowItems.splice(activeIndex, 1);

			const targetRowItems = [...newItemsByRowId[overRowId]];
			targetRowItems.splice(overIndex, 0, movedItem);

			const updatedItemsByRowId = {
				...newItemsByRowId,
				[activeRowId]: sourceRowItems,
				[overRowId]: targetRowItems,
			};

			const newRowIds = handleClearEmptyRows(updatedItemsByRowId, rowIds);
			const newItemSpansById = handleUpdateItemSpans(
				updatedItemsByRowId,
				activeRowId,
				overRowId
			);
			handleLayoutChange(newRowIds, updatedItemsByRowId, newItemSpansById);

			setItemsByRowId(updatedItemsByRowId);
		},
		[
			handleClearDragState,
			handleClearEmptyRows,
			handleLayoutChange,
			handleUpdateItemSpans,
			itemsByRowId,
			rowIds,
		]
	);

	/**
	 * Handles drag end when item is dropped on horizontal indicator (creates new row)
	 */
	const handleDragEndOverNewRow = useCallback(
		(activeId: string, activeRowId: string, overIndex: number) => {
			const newRowId = generateUniqueId();

			const activeItem = itemsById[activeId];
			if (!activeItem) return;

			const newRowIds = [...rowIds];
			newRowIds.splice(overIndex, 0, newRowId);

			const newItemsByRowId = { ...itemsByRowId };

			const sourceRowItems = [...newItemsByRowId[activeRowId]];
			const activeIndex = sourceRowItems.findIndex(
				(item) => item.id === activeId
			);
			const [movedItem] = sourceRowItems.splice(activeIndex, 1);

			newItemsByRowId[newRowId] = [movedItem];

			const updatedItemsByRowId = {
				...newItemsByRowId,
				[activeRowId]: sourceRowItems,
			};

			const newItemSpansById = handleUpdateItemSpans(
				updatedItemsByRowId,
				activeRowId,
				newRowId
			);

			const updatedRowIds = handleClearEmptyRows(
				updatedItemsByRowId,
				newRowIds
			);
			setItemsByRowId(updatedItemsByRowId);
			handleLayoutChange(updatedRowIds, updatedItemsByRowId, newItemSpansById);
		},
		[
			itemsById,
			rowIds,
			itemsByRowId,
			handleUpdateItemSpans,
			handleClearEmptyRows,
			handleLayoutChange,
		]
	);

	/**
	 * Main drag end handler - routes to appropriate handler based on drop target
	 */
	const handleDragEnd = useCallback(
		({ over }: DragEndEvent) => {
			if (readOnly) {
				handleClearDragState();
				return;
			}

			const { activeId, activeRowId, overRowId, overIndex, overType } =
				dragState;

			if (!activeId || !activeRowId || !over) {
				handleClearDragState();
				return;
			}

			if (
				overType === 'vertical-indicator' &&
				overRowId !== null &&
				overIndex !== null
			) {
				if (activeRowId === overRowId) {
					handleDragEndOverSameRow(activeId, activeRowId, overIndex);
				} else {
					handleDragEndBetweenRows(activeId, activeRowId, overRowId, overIndex);
				}
			} else if (overType === 'horizontal-indicator' && overIndex !== null) {
				handleDragEndOverNewRow(activeId, activeRowId, overIndex);
			}

			handleClearDragState();
		},
		[
			readOnly,
			dragState,
			handleClearDragState,
			handleDragEndOverSameRow,
			handleDragEndBetweenRows,
			handleDragEndOverNewRow,
		]
	);

	/**
	 * Handles drag cancellation
	 */
	const handleDragCancel = useCallback(() => {
		handleClearDragState();
	}, [handleClearDragState]);

	/**
	 * Initiates resize operation - stores initial state and sets up resize mode
	 */
	const handleResizeStart = useCallback(
		(indicatorId: string, mouseX: number) => {
			if (readOnly) return;

			const rowId = findRowForIndicator(indicatorId, itemsByRowId);
			if (!rowId) {
				return;
			}

			const itemId = extractItemIdFromIndicator(indicatorId);
			if (!itemId) {
				return;
			}

			const itemIndex = itemsByRowId[rowId].findIndex(
				(item) => item.id === itemId
			);
			if (itemIndex === -1) {
				return;
			}

			// Store initial spans and widths for this row
			const rowItems = itemsByRowId[rowId];
			const initialSpans: Record<string, number> = {};
			const initialTempWidths: Record<string, string> = {};
			rowItems.forEach((item) => {
				const span = itemSpansById[item.id] || 0;
				initialSpans[item.id] = span;
				initialTempWidths[item.id] =
					`${(1 / (TOTAL_NUMBER_OF_COLUMNS / span)) * 100}%`;
			});

			setResizeState({
				isResizing: true,
				resizeIndicatorId: indicatorId,
				resizeRowId: rowId,
				resizeItemIndex: itemIndex,
				initialSpans,
				initialMouseX: mouseX,
				tempWidths: initialTempWidths,
			});
		},
		[readOnly, itemsByRowId, itemSpansById]
	);

	/**
	 * Handles resize movement - provides smooth visual feedback during resize
	 */
	const handleResizeMove = useCallback(
		(mouseX: number) => {
			if (
				!resizeState.isResizing ||
				!resizeState.resizeRowId ||
				!resizeState.initialMouseX ||
				!resizeState.initialSpans ||
				!resizeState.tempWidths
			) {
				return;
			}

			const deltaX = mouseX - resizeState.initialMouseX;
			const rowId = resizeState.resizeRowId;
			const rowItems = itemsByRowId[rowId];

			// Calculate how many columns the mouse movement represents
			const deltaColumns =
				(deltaX / window.innerWidth) * TOTAL_NUMBER_OF_COLUMNS;

			const newTempWidths = { ...resizeState.tempWidths };
			const itemIndex = resizeState.resizeItemIndex!;
			const isBeforeIndicator = resizeState.resizeIndicatorId!.startsWith(
				VERTICAL_INDICATOR_BEFORE_ID_PREFIX
			);

			// Determine which items to resize
			const leftItemIndex = isBeforeIndicator ? itemIndex - 1 : itemIndex;
			const rightItemIndex = isBeforeIndicator ? itemIndex : itemIndex + 1;

			if (leftItemIndex >= 0 && rightItemIndex < rowItems.length) {
				const leftItem = rowItems[leftItemIndex];
				const rightItem = rowItems[rightItemIndex];

				const leftItemSpan = resizeState.initialSpans[leftItem.id];
				const rightItemSpan = resizeState.initialSpans[rightItem.id];

				// Calculate new spans with smooth decimal values
				let newLeftSpan = leftItemSpan + deltaColumns;
				let newRightSpan = rightItemSpan - deltaColumns;

				// Apply minimum span constraint for visual feedback
				if (newLeftSpan < MIN_ITEM_SPAN) {
					newLeftSpan = MIN_ITEM_SPAN;
					newRightSpan = leftItemSpan + rightItemSpan - MIN_ITEM_SPAN;
				}
				if (newRightSpan < MIN_ITEM_SPAN) {
					newRightSpan = MIN_ITEM_SPAN;
					newLeftSpan = leftItemSpan + rightItemSpan - MIN_ITEM_SPAN;
				}

				// Update temporary widths for smooth visual feedback
				newTempWidths[leftItem.id] =
					`${(1 / (TOTAL_NUMBER_OF_COLUMNS / newLeftSpan)) * 100}%`;
				newTempWidths[rightItem.id] =
					`${(1 / (TOTAL_NUMBER_OF_COLUMNS / newRightSpan)) * 100}%`;

				// Update the temporary widths state for smooth visual updates
				setResizeState((prev) => ({
					...prev,
					tempWidths: newTempWidths,
				}));
			}
		},
		[resizeState, itemsByRowId]
	);

	/**
	 * Completes resize operation - snaps to grid and updates actual spans
	 */
	const handleResizeEnd = useCallback(() => {
		if (
			!resizeState.isResizing ||
			!resizeState.resizeRowId ||
			!resizeState.initialSpans
		)
			return;

		const rowItems = itemsByRowId[resizeState.resizeRowId];
		const itemIndex = resizeState.resizeItemIndex!;
		const isBeforeIndicator = resizeState.resizeIndicatorId!.startsWith(
			VERTICAL_INDICATOR_BEFORE_ID_PREFIX
		);

		// Calculate final spans by snapping to proper grid values
		const leftItemIndex = isBeforeIndicator ? itemIndex - 1 : itemIndex;
		const rightItemIndex = isBeforeIndicator ? itemIndex : itemIndex + 1;

		if (leftItemIndex >= 0 && rightItemIndex < rowItems.length) {
			const leftItem = rowItems[leftItemIndex];
			const rightItem = rowItems[rightItemIndex];

			const leftItemSpan = resizeState.initialSpans[leftItem.id];
			const rightItemSpan = resizeState.initialSpans[rightItem.id];

			// Calculate the final delta based on current mouse position
			const initialLeftWidth =
				(1 / (TOTAL_NUMBER_OF_COLUMNS / leftItemSpan)) * 100;
			const currentLeftWidth = resizeState.tempWidths?.[leftItem.id]
				? parseFloat(resizeState.tempWidths[leftItem.id])
				: initialLeftWidth;

			const deltaColumns = Math.round(
				(currentLeftWidth - initialLeftWidth) / (100 / TOTAL_NUMBER_OF_COLUMNS)
			);

			// Calculate final spans with proper snapping
			let newLeftSpan = leftItemSpan + deltaColumns;
			let newRightSpan = rightItemSpan - deltaColumns;

			// Apply minimum span constraint
			if (newLeftSpan < MIN_ITEM_SPAN) {
				newLeftSpan = MIN_ITEM_SPAN;
				newRightSpan = leftItemSpan + rightItemSpan - MIN_ITEM_SPAN;
			}
			if (newRightSpan < MIN_ITEM_SPAN) {
				newRightSpan = MIN_ITEM_SPAN;
				newLeftSpan = leftItemSpan + rightItemSpan - MIN_ITEM_SPAN;
			}

			// Update the actual spans
			const newSpans = { ...resizeState.initialSpans };
			newSpans[leftItem.id] = newLeftSpan;
			newSpans[rightItem.id] = newRightSpan;

			const newItemSpansById = { ...itemSpansById, ...newSpans };

			setItemSpansById(newItemSpansById);
			handleLayoutChange(rowIds, itemsByRowId, newItemSpansById);
		}

		setResizeState({
			isResizing: false,
			resizeIndicatorId: null,
			resizeRowId: null,
			resizeItemIndex: null,
			initialSpans: null,
			initialMouseX: null,
			tempWidths: null,
		});
	}, [
		resizeState.isResizing,
		resizeState.resizeRowId,
		resizeState.initialSpans,
		resizeState.resizeItemIndex,
		resizeState.resizeIndicatorId,
		resizeState.tempWidths,
		itemsByRowId,
		itemSpansById,
		handleLayoutChange,
		rowIds,
	]);

	/**
	 * Global mouse event listeners for resize functionality
	 * Attaches listeners when resize starts, removes when resize ends
	 */
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (!resizeState.isResizing) return;

		const handleMouseMove = (event: MouseEvent) => {
			handleResizeMove(event.clientX);
		};

		const handleMouseUp = () => {
			handleResizeEnd();
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		// eslint-disable-next-line consistent-return
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [resizeState.isResizing, handleResizeMove, handleResizeEnd]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={customCollisionDetectionAlgorithm}
			measuring={{
				droppable: {
					strategy: MeasuringStrategy.Always,
				},
			}}
			onDragStart={handleDragStart}
			onDragOver={handleDragOver}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
		>
			<Stack spacing={0}>
				{rowIds.map((rowId, rowIndex) => {
					const isFirstRow = rowIndex === 0;
					const isLastRow = rowIndex === rowIds.length - 1;

					const numberOfItemsInRow = itemsByRowId[rowId].length;
					return (
						<DnDRow
							key={rowId}
							id={rowId}
							items={itemsByRowId[rowId]}
							isFirstRow={isFirstRow}
							isLastRow={isLastRow}
							readOnly={readOnly}
						>
							<SortableContext
								items={itemsByRowId[rowId].map((item) => item.id)}
								strategy={horizontalListSortingStrategy}
							>
								{itemsByRowId[rowId].map((item, itemIndex) => {
									const isFirstItem = itemIndex === 0;
									const isLastItem =
										itemIndex === itemsByRowId[rowId].length - 1;

									const width = getItemWidth(item.id);
									if (!width) return null;

									return (
										<DnDItemComponent<T>
											key={item.id}
											item={item}
											width={width}
											isActive={dragState.activeId === item.id}
											isFirstItem={isFirstItem}
											isLastItem={isLastItem}
											isHoveringOverSameRow={
												dragState.overRowId === dragState.activeRowId
											}
											isIndicatorDisabled={
												numberOfItemsInRow === MAX_ITEMS_PER_ROW || readOnly
											}
											readOnly={readOnly}
											renderItem={renderItem}
											onResizeStart={readOnly ? undefined : handleResizeStart}
										/>
									);
								})}
							</SortableContext>
						</DnDRow>
					);
				})}
				{dragState.activeId && !readOnly && (
					<DragOverlay>
						<DnDItemInternal<T>
							item={itemsById[dragState.activeId]}
							isActive={true}
							isFirstItem={false}
							isLastItem={false}
							readOnly={readOnly}
							renderItem={renderItem}
							isOverlay
						/>
					</DragOverlay>
				)}
			</Stack>
		</DndContext>
	);
}
